import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Alert, Box, Button } from "@mui/material";
import {
  CheckCircleOutlineOutlined,
  CurrencyRupeeOutlined,
  ErrorOutlineOutlined,
  HelpOutlineOutlined,
  ReceiptLongOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import InvoiceTable from "./InvoiceTable";
import AddInvoice from "./AddInvoice";
import styles from "./InvoiceProcess.module.css";
import { useSelector } from "react-redux";
import { API_ENDPOINT } from "../../../services/api/resources";

const InvoiceProcess = () => {
  const [view, setView] = useState("LIST");
  const [vendorInvoices, setVendorInvoices] = useState([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const loginData = useSelector((state) => state.loginData.value);
  const authToken = loginData?.auth_token;
  const vendorToken =
    loginData?.user?.token ||
    loginData?.user?.user_token ||
    loginData?.vendor_token ||
    loginData?.user_token;

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const summaryFetchKeyRef = useRef("");

  const fetchSummary = useCallback(async () => {
    setSummaryError("");

    if (!vendorToken) {
      setSummary(null);
      return;
    }

    setSummaryLoading(true);
    try {
      const res = await axios.post(
        API_ENDPOINT.get_vendor_invoice_conciliation_summary,
        { vendor_token: vendorToken },
        authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined,
      );
      setSummary(res?.data ?? null);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.response ||
        err?.message ||
        "Failed to load invoice summary";
      setSummary(null);
      setSummaryError(String(msg));
    } finally {
      setSummaryLoading(false);
    }
  }, [authToken, vendorToken]);

  useEffect(() => {
    if (view !== "LIST") return;
    if (!vendorToken) return;
    // React 18 StrictMode runs effects twice in dev; avoid double API calls.
    const key = `${vendorToken}|${authToken || ""}`;
    if (summaryFetchKeyRef.current === key) return;
    summaryFetchKeyRef.current = key;
    fetchSummary();
  }, [authToken, fetchSummary, vendorToken, view]);

  const summaryCards = useMemo(() => {
    const s = summary || {};
    const toInt = (v) => {
      const n = Number(v ?? 0);
      return Number.isFinite(n) ? n : 0;
    };

    const total = Math.max(1, toInt(s?.total_invoices_created));
    const pct = (count) => Math.round((toInt(count) / total) * 100);

    const amountFormatted =
      String(s?.total_invoice_amount_formatted ?? "").trim() ||
      (() => {
        const n = Number(s?.total_invoice_amount);
        return Number.isFinite(n) ? n.toLocaleString("en-IN") : "-";
      })();

    return [
      {
        key: "total_invoices",
        label: "Total Invoices",
        value: toInt(s?.total_invoices_created).toLocaleString("en-IN"),
        badge: "Created",
        sub: String(s?.vendor_name ?? "All invoices").trim() || "All invoices",
        color: "blue",
        icon: <ReceiptLongOutlined className={styles.kpiIcon} />,
      },
      {
        key: "total_amount",
        label: "Total Amount (INR)",
        value: amountFormatted === "-" ? "-" : `₹ ${amountFormatted}`,
        badge: "INR",
        sub: "Total invoice amount",
        color: "purple",
        icon: <CurrencyRupeeOutlined className={styles.kpiIcon} />,
      },
      {
        key: "matched",
        label: "Matched",
        value: toInt(s?.matched_count).toLocaleString("en-IN"),
        badge: `${pct(s?.matched_count)}%`,
        sub: "Fully matched",
        color: "green",
        icon: <CheckCircleOutlineOutlined className={styles.kpiIcon} />,
      },
      {
        key: "partial",
        label: "Partially Matched",
        value: toInt(s?.partially_matched_count).toLocaleString("en-IN"),
        badge: `${pct(s?.partially_matched_count)}%`,
        sub: "Needs review",
        color: "orange",
        icon: <WarningAmberOutlined className={styles.kpiIcon} />,
      },
      {
        key: "mismatch",
        label: "Exception Found",
        value: toInt(s?.mismatched_count).toLocaleString("en-IN"),
        badge: `${pct(s?.mismatched_count)}%`,
        sub: "Exception Found ",
        color: "red",
        icon: <ErrorOutlineOutlined className={styles.kpiIcon} />,
      },
      {
        key: "unverified",
        label: "Unverified",
        value: toInt(s?.unverified_count).toLocaleString("en-IN"),
        badge: `${pct(s?.unverified_count)}%`,
        sub: "Awaiting verification",
        color: "gray",
        icon: <HelpOutlineOutlined className={styles.kpiIcon} />,
      },
    ];
  }, [summary]);
  const handleSaveInvoice = () => {
    setVendorInvoices([]);
    setRefreshToken((prev) => prev + 1);
    setView("LIST");
  };

  useEffect(() => {
    const chatActionRaw = sessionStorage.getItem("CHAT_ACTION");
    if (!chatActionRaw) return;

    const { action } = JSON.parse(chatActionRaw);

    if (action === "add_invoice") {
      setView("ADD");
      sessionStorage.removeItem("CHAT_ACTION");
    }
  }, []);

  return (
    <Box className={styles.container}>
      {view === "LIST" && (
        <>
          {/* HEADER */}
          <Box className={styles.header}>
            <h1 className={styles.pageTitle}>Invoice Reconciliation</h1>
            <Button
              size="large"
              variant="contained"
              onClick={() => setView("ADD")}
              sx={{ fontSize: "12px", fontFamily: "GilroyBold" }}
            >
              Add Documents
            </Button>
          </Box>

          <Box className={styles.kpiRow}>
            {summaryCards.map((card) => (
              <Box
                key={card.key}
                className={`${styles.kpiCard} ${styles[`kpiCard_${card.color}`]}`}
              >
                <Box className={styles.kpiCardHeader}>
                  <Box
                    className={`${styles.kpiIconWrap} ${styles[`kpiIconWrap_${card.color}`]}`}
                  >
                    {card.icon}
                  </Box>
                </Box>
                <Box className={styles.kpiValueRow}>
                  {summaryLoading ? (
                    <span className={styles.kpiSkeletonValue} />
                  ) : (
                    <span className={styles.kpiValue}>{card.value}</span>
                  )}
                  {summaryLoading ? (
                    <span className={styles.kpiSkeletonBadge} />
                  ) : (
                    <span
                      className={`${styles.kpiBadge} ${styles[`kpiBadge_${card.color}`]}`}
                    >
                      {card.badge}
                    </span>
                  )}
                </Box>
                <Box className={styles.kpiLabelRow}>
                  <span className={styles.kpiLabel}>{card.label}</span>
                </Box>
                <Box className={styles.kpiSubRow}>
                  {summaryLoading ? (
                    <span className={styles.kpiSkeletonSub} />
                  ) : (
                    <span className={styles.kpiSub}>{card.sub}</span>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {summaryError ? (
            <Box className={styles.kpiErrorRow}>
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ fontFamily: "gilroyMedium", fontSize: "14px" }}
              >
                {summaryError}
              </Alert>
            </Box>
          ) : null}

          {/* TABLE */}
          <InvoiceTable
            data={vendorInvoices}
            VendorToken={vendorToken}
            refreshToken={refreshToken}
          />
        </>
      )}

      {view === "ADD" && (
        <AddInvoice
          onCancel={() => setView("LIST")}
          onSave={handleSaveInvoice}
          VendorToken={vendorToken}
        />
      )}
    </Box>
  );
};

export default InvoiceProcess;
